// nulllabs override of livekit-examples/meet@665e1cb7 app/page.tsx
// consumers: browser landing page (public at meet.nulllabs.cloud, LAN at
//            meet.cluster)
// deps: PageClientImpl.tsx override (consumes ?secret= on the room page),
//       app/api/connection-details/route.ts override (enforces the gate)
//
// Changes vs upstream:
//  1. Gate-aware: without ?secret= the create UI is hidden and a private-server
//     notice is shown instead. This is UX ONLY — the authoritative check is
//     server-side in the connection-details route. Without it, strangers who
//     find the public URL click "Start Meeting" and hit a bare 403, which reads
//     like a broken site and invites probing.
//  2. Preserves the query string into the room URL so the host's ?secret=
//     rides along. Host bookmark: https://meet.nulllabs.cloud/?secret=<pass>
//  3. Upstream's demo copy ("Try LiveKit Meet for free with our live demo
//     project", "Hosted on LiveKit Cloud") is replaced — this instance runs on
//     a private LiveKit project, so that text was simply untrue here.
//  4. Drops the "Custom" tab: it connects to an operator-supplied LiveKit URL
//     with a pasted token, which is dead weight on a single-tenant server.
//     Restore it from upstream if you ever want to point this UI elsewhere.
'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

// Mirrors the /api/rooms payload (lib/nulllabs-rooms.ts LiveRoom + the link/QR
// the route adds). `huly` drives the "not end-to-end encrypted" warning.
interface LiveRoom {
  name: string;
  code: string;
  numParticipants: number;
  huly: boolean;
  link: string;
  qrSvg: string;
}

// Live rooms include Huly meetings: Huly's `love` service and this app share
// one LiveKit project, so a Huly Virtual Office room shows up here and its
// guest link lets an outsider join WITHOUT a Huly account — which Huly itself
// refuses to do (its /getToken rejects tokens carrying extra.guest="true").
function ActiveMeetings(props: { secret: string }) {
  const [rooms, setRooms] = useState<LiveRoom[] | null>(null);
  const [copied, setCopied] = useState<string>('');

  React.useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/rooms?secret=${encodeURIComponent(props.secret)}`);
        if (!r.ok) {
          if (!stop) setRooms([]);
          return;
        }
        const d = await r.json();
        if (!stop) setRooms(d.rooms ?? []);
      } catch {
        if (!stop) setRooms([]);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [props.secret]);

  if (rooms === null) return <p style={{ opacity: 0.7 }}>Looking for active meetings…</p>;
  if (rooms.length === 0) return null;

  return (
    <div style={{ marginTop: '1.5rem', width: '100%' }}>
      <h3 style={{ marginBottom: '0.5rem' }}>Active meetings</h3>
      <p style={{ marginTop: 0, opacity: 0.7 }}>
        Share a guest link — recipients join straight from it, no account and no
        passphrase.
      </p>
      {rooms.map((r) => {
        // Prefer the server-computed link: it is built from the Host header the
        // guest will actually use, so it stays correct behind the gateway.
        const link = r.link || `${window.location.origin}/j/${r.code}`;
        return (
          <div
            key={r.code}
            style={{
              display: 'flex',
              gap: '1rem',
              padding: '0.75rem 0',
              borderTop: '1px solid rgba(255,255,255,0.15)',
              alignItems: 'flex-start',
            }}
          >
            {r.qrSvg && (
              // Point a phone camera at this instead of typing anything.
              <div
                style={{ background: '#fff', padding: '4px', borderRadius: '4px', flexShrink: 0 }}
                dangerouslySetInnerHTML={{ __html: r.qrSvg }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1 }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.75, wordBreak: 'break-all' }}>
                {r.name}
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                {r.numParticipants} participant(s)
                {r.huly ? (
                  // Huly's love service never enables LiveKit E2EE, and
                  // encryption is per-room all-or-nothing: an encrypted guest
                  // and a Huly participant would see each other as black
                  // frames. Say so instead of letting anyone assume otherwise.
                  <span style={{ marginLeft: '0.5rem', color: '#e8a33d' }}>
                    · Huly meeting — not end-to-end encrypted
                  </span>
                ) : (
                  <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>
                    · E2EE only if the link carries a key
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <code style={{ flex: 1, wordBreak: 'break-all' }}>{link}</code>
                <button
                  className="lk-button"
                  onClick={() => {
                    navigator.clipboard?.writeText(link);
                    setCopied(r.code);
                  }}
                >
                  {copied === r.code ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HostControls(props: { secret: string }) {
  const router = useRouter();
  // E2EE DEFAULT ON for rooms started here. 32 symbols over a 36-char alphabet
  // is ~165 bits from a CSPRNG (see lib/client-utils.ts override) — upstream's
  // 64 only looked stronger because it came from Math.random(). Shorter also
  // keeps the guest link scannable, since the key rides in the URL fragment.
  const [e2ee, setE2ee] = useState(true);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(32));

  const startMeeting = () => {
    // carry ?secret= (and anything else) onto the room URL
    const search = window.location.search;
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}${search}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${generateRoomId()}${search}`);
    }
  };

  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>
        Start a room, then share the resulting link. Guests join straight from the
        link — no account, and no passphrase needed on their side. Meetings
        already running in Huly appear below with their own guest links.
      </p>
      <button style={{ marginTop: '1rem' }} className="lk-button" onClick={startMeeting}>
        Start Meeting
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Enable end-to-end encryption</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
      <ActiveMeetings secret={props.secret} />
    </div>
  );
}

function PrivateNotice() {
  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>
        This is a private meeting server. Rooms can only be created by the
        operator.
      </p>
      <p style={{ marginTop: '0.5rem', opacity: 0.7 }}>
        If you were sent a meeting link, open that link directly — it will take
        you into the room.
      </p>
    </div>
  );
}

function Landing() {
  const searchParams = useSearchParams();
  const secret = searchParams?.get('secret') ?? '';
  return secret ? <HostControls secret={secret} /> : <PrivateNotice />;
}

export default function Page() {
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <img src="/images/livekit-meet-home.svg" alt="Meet" width="360" height="45" />
          <h2>Private video conferencing.</h2>
        </div>
        <Suspense fallback="Loading">
          <Landing />
        </Suspense>
      </main>
      <footer data-lk-theme="default">
        Built on{' '}
        <a href="https://github.com/livekit/components-js?ref=meet" rel="noopener">
          LiveKit&nbsp;Components
        </a>
        .
      </footer>
    </>
  );
}
